'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Filter, Plus, MapPin, Tag, ChevronDown, Loader2, MessageSquare, X } from 'lucide-react';
import Link from 'next/link';

interface WantedPost {
  id: string;
  title: string;
  category: string;
  category_slug: string;
  description: string;
  budget_min: number | null;
  budget_max: number | null;
  budget_note: string | null;
  location: string | null;
  condition_wanted: string;
  brand_preference: string | null;
  contact_preference: string;
  post_status: string;
  reply_count: number;
  created_at: string;
  user_id: string;
  user_profiles?: {
    full_name: string;
    store_name: string | null;
    location: string | null;
  };
}

const CATEGORIES = [
  { label: 'All Categories', slug: '' },
  { label: 'Crates', slug: 'crates' },
  { label: 'Bite Equipment', slug: 'bite-equipment' },
  { label: 'Leads & Collars', slug: 'leads-collars' },
  { label: 'E-Collars', slug: 'e-collars' },
  { label: 'Training Equipment', slug: 'training-equipment' },
  { label: 'Dogs / Puppies', slug: 'dogs-puppies' },
  { label: 'Breeding / Stud / Litter', slug: 'breeding-stud-litter' },
];

const conditionLabels: Record<string, string> = {
  new: 'New only',
  'like-new': 'Like New',
  used: 'Used OK',
  any: 'Any condition',
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d < 1) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 7) return `${d} days ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export default function WantedPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<WantedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [contactModal, setContactModal] = useState<WantedPost | null>(null);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    let query = supabase
      .from('wanted_posts')
      .select('*, user_profiles(full_name, store_name, location)')
      .eq('post_status', 'active')
      .order('created_at', { ascending: false });

    if (selectedCategory) query = query.eq('category_slug', selectedCategory);
    if (search.trim()) query = query.ilike('title', `%${search.trim()}%`);

    const { data } = await query;
    setPosts((data as unknown as WantedPost[]) || []);
    setLoading(false);
  }, [search, selectedCategory]);

  useEffect(() => {
    const timer = setTimeout(fetchPosts, 300);
    return () => clearTimeout(timer);
  }, [fetchPosts]);

  async function handleContactBuyer(e: React.FormEvent) {
    e.preventDefault();
    if (!contactModal || !contactMessage.trim()) return;
    setContactSending(true);
    setContactError('');
    try {
      const supabase = createClient();
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated');

      const { error } = await supabase.from('wanted_enquiries').insert({
        wanted_post_id: contactModal.id,
        sender_id: currentUser.id,
        message: contactMessage.trim(),
      });
      if (error) throw error;

      await supabase.from('wanted_posts').update({ reply_count: (contactModal.reply_count || 0) + 1 }).eq('id', contactModal.id);

      // Send in-app notification to the wanted post owner
      const senderProfile = await supabase
        .from('user_profiles')
        .select('full_name, store_name')
        .eq('id', currentUser.id)
        .maybeSingle();
      const senderName = senderProfile?.data?.store_name || senderProfile?.data?.full_name || 'Someone';

      await supabase.from('notifications').insert({
        user_id: contactModal.user_id,
        type: 'wanted_reply',
        title: 'New reply to your wanted post',
        body: `${senderName} responded to your wanted post: "${contactModal.title}"`,
        link: '/wanted/my-posts',
      }).catch(() => {});

      // Send email notification to the wanted post owner
      const ownerProfile = await supabase
        .from('user_profiles')
        .select('email')
        .eq('id', contactModal.user_id)
        .maybeSingle();
      const ownerEmail = ownerProfile?.data?.email;
      if (ownerEmail) {
        await supabase.functions.invoke('send-notification-email', {
          body: {
            to: ownerEmail,
            type: 'wanted_reply',
            data: {
              senderName,
              postTitle: contactModal.title,
              messagePreview: contactMessage.trim().slice(0, 120),
            },
            referenceId: contactModal.id,
          },
        }).catch(() => {});
      }

      setContactSent(true);
      setTimeout(() => { setContactModal(null); setContactSent(false); setContactMessage(''); }, 2000);
    } catch (err: any) {
      setContactError(err?.message || 'Failed to send message');
    } finally {
      setContactSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-[hsl(222,47%,6%)]">
      <Topbar />
      <main className="pt-16">
        {/* Header */}
        <div className="border-b border-[hsl(222,25%,18%)] bg-[hsl(222,40%,9%)]">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">Wanted</h1>
                <p className="text-slate-400 text-sm">Browse what buyers are looking for — respond if you have it</p>
              </div>
              <Link
                href="/wanted/create"
                className="flex items-center gap-2 bg-[hsl(28,90%,52%)] hover:bg-[hsl(28,90%,45%)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition-all duration-150 active:scale-95 shrink-0"
              >
                <Plus className="w-4 h-4" />
                Post Wanted
              </Link>
            </div>

            {/* Search + filter bar */}
            <div className="flex items-center gap-3 mt-5">
              <div className="relative flex-1 max-w-lg">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search wanted posts..."
                  className="w-full bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-[hsl(28,90%,52%)]/60 focus:ring-1 focus:ring-[hsl(28,90%,52%)]/30 transition-all duration-150"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all duration-150 ${showFilters ? 'bg-[hsl(28,90%,52%)]/10 border-[hsl(28,90%,52%)]/40 text-[hsl(28,90%,60%)]' : 'border-[hsl(222,25%,18%)] text-slate-400 hover:text-white hover:border-slate-500'}`}
              >
                <Filter className="w-4 h-4" />
                Filters
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Category filter */}
            {showFilters && (
              <div className="mt-4 flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.slug || 'all'}
                    onClick={() => setSelectedCategory(cat.slug)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
                      selectedCategory === cat.slug
                        ? 'bg-[hsl(28,90%,52%)]/15 border-[hsl(28,90%,52%)]/40 text-[hsl(28,90%,60%)]'
                        : 'border-[hsl(222,25%,18%)] text-slate-400 hover:text-white hover:border-slate-500'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Posts grid */}
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-[hsl(28,90%,52%)] animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-slate-500" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2">No wanted posts yet</h3>
              <p className="text-sm text-slate-500 mb-5 max-w-xs">
                {search || selectedCategory ? 'No posts match your filters.' : 'Be the first to post what you\'re looking for.'}
              </p>
              <Link
                href="/wanted/create"
                className="flex items-center gap-2 bg-[hsl(28,90%,52%)] hover:bg-[hsl(28,90%,45%)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition-all duration-150"
              >
                <Plus className="w-4 h-4" />
                Post Wanted Ad
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {posts.map((post) => (
                <div key={post.id} className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-5 hover:border-slate-500 transition-all duration-200 flex flex-col gap-3">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="inline-block text-xs font-medium text-[hsl(28,90%,60%)] bg-[hsl(28,90%,52%)]/10 border border-[hsl(28,90%,52%)]/20 px-2 py-0.5 rounded-md mb-2">
                        {post.category}
                      </span>
                      <h3 className="text-sm font-semibold text-white leading-snug">{post.title}</h3>
                    </div>
                  </div>

                  {/* Description */}
                  {post.description && (
                    <p className="text-xs text-slate-400 line-clamp-2">{post.description}</p>
                  )}

                  {/* Meta */}
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-slate-500">
                    {(post.budget_min || post.budget_max) && (
                      <span className="flex items-center gap-1 text-white font-medium">
                        <Tag className="w-3 h-3 text-slate-500" />
                        {post.budget_min && post.budget_max
                          ? `£${post.budget_min.toLocaleString()} – £${post.budget_max.toLocaleString()}`
                          : post.budget_max
                          ? `Up to £${post.budget_max.toLocaleString()}`
                          : `From £${post.budget_min?.toLocaleString()}`}
                      </span>
                    )}
                    {post.budget_note && !post.budget_min && !post.budget_max && (
                      <span className="text-white font-medium">{post.budget_note}</span>
                    )}
                    {post.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {post.location}
                      </span>
                    )}
                    <span>{conditionLabels[post.condition_wanted] || post.condition_wanted}</span>
                    {post.brand_preference && (
                      <span>Prefers: {post.brand_preference}</span>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-2 border-t border-[hsl(222,25%,18%)] mt-auto">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="text-slate-400 font-medium">{post.user_profiles?.store_name || post.user_profiles?.full_name || 'Anonymous'}</span>
                      <span>·</span>
                      <span>{timeAgo(post.created_at)}</span>
                      {post.reply_count > 0 && (
                        <>
                          <span>·</span>
                          <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{post.reply_count}</span>
                        </>
                      )}
                    </div>
                    {user && user.id !== post.user_id && (
                      <button
                        onClick={() => { setContactModal(post); setContactSent(false); setContactMessage(''); setContactError(''); }}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(28,90%,60%)] hover:text-[hsl(28,90%,52%)] bg-[hsl(28,90%,52%)]/10 hover:bg-[hsl(28,90%,52%)]/20 border border-[hsl(28,90%,52%)]/20 px-3 py-1.5 rounded-lg transition-all duration-150"
                      >
                        I have this
                      </button>
                    )}
                    {!user && (
                      <Link
                        href="/login"
                        className="text-xs text-slate-500 hover:text-white transition-colors"
                      >
                        Sign in to reply
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* Contact buyer modal */}
      {contactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setContactModal(null)} />
          <div className="relative bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-2xl w-full max-w-md shadow-2xl shadow-black/50">
            <div className="flex items-center justify-between p-5 border-b border-[hsl(222,25%,18%)]">
              <div>
                <h2 className="font-semibold text-white text-base">Reply to Wanted Post</h2>
                <p className="text-xs text-slate-500 mt-0.5 truncate max-w-xs">{contactModal.title}</p>
              </div>
              <button onClick={() => setContactModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-all duration-150">
                <X className="w-4 h-4" />
              </button>
            </div>
            {contactSent ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">Message Sent</h3>
                <p className="text-sm text-slate-400">The buyer has been notified of your reply.</p>
              </div>
            ) : (
              <form onSubmit={handleContactBuyer} className="p-5">
                {contactError && <p className="text-xs text-red-400 mb-3">{contactError}</p>}
                <p className="text-xs text-slate-400 mb-3">
                  Let the buyer know you have what they're looking for. Include details, price, and condition.
                </p>
                <textarea
                  rows={5}
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Hi, I have exactly what you're looking for. It's in great condition and priced at..."
                  className="w-full bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-600 rounded-xl px-3.5 py-3 text-sm focus:outline-none focus:border-[hsl(28,90%,52%)]/60 focus:ring-1 focus:ring-[hsl(28,90%,52%)]/30 transition-all duration-150 resize-none mb-4"
                  required
                  minLength={10}
                />
                <button
                  type="submit"
                  disabled={contactSending || !contactMessage.trim()}
                  className="w-full flex items-center justify-center gap-2 bg-[hsl(28,90%,52%)] hover:bg-[hsl(28,90%,45%)] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl text-sm transition-all duration-150 active:scale-95"
                >
                  {contactSending ? <><Loader2 className="w-4 h-4 animate-spin" />Sending...</> : 'Send Reply'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
